import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ── Thunks ─────────────────────────────────────────────────────────────────

export const fetchExecutivePlan = createAsyncThunk(
  'executivePlan/fetch',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/executive-plan`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to load executive plan');
    }
  }
);

export const createPhase = createAsyncThunk(
  'executivePlan/createPhase',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/executive-plan/phases`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create phase');
    }
  }
);

export const updatePhase = createAsyncThunk(
  'executivePlan/updatePhase',
  async ({ projectId, phaseId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}/executive-plan/phases/${phaseId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update phase');
    }
  }
);

export const deletePhase = createAsyncThunk(
  'executivePlan/deletePhase',
  async ({ projectId, phaseId }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/executive-plan/phases/${phaseId}`);
      return phaseId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete phase');
    }
  }
);

export const addStoryToPhase = createAsyncThunk(
  'executivePlan/addStory',
  async ({ projectId, phaseId, taskId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/projects/${projectId}/executive-plan/phases/${phaseId}/stories`,
        { task_id: taskId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to link story');
    }
  }
);

export const removeStoryFromPhase = createAsyncThunk(
  'executivePlan/removeStory',
  async ({ projectId, phaseId, taskId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/projects/${projectId}/executive-plan/phases/${phaseId}/stories/${taskId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to remove story');
    }
  }
);

export const searchStories = createAsyncThunk(
  'executivePlan/searchStories',
  async ({ projectId, query }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/executive-plan/stories/search`, {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to search stories');
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────────────

const initialState = {
  plan: null,
  loading: false,
  saving: false,
  error: null,
  storySearchResults: [],
  storySearchLoading: false,
};

const executivePlanSlice = createSlice({
  name: 'executivePlan',
  initialState,
  reducers: {
    clearPlan(state) {
      state.plan = null;
      state.error = null;
    },
    clearStorySearch(state) {
      state.storySearchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch plan
      .addCase(fetchExecutivePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExecutivePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plan = action.payload;
      })
      .addCase(fetchExecutivePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create phase
      .addCase(createPhase.pending, (state) => { state.saving = true; })
      .addCase(createPhase.fulfilled, (state, action) => {
        state.saving = false;
        if (state.plan) {
          state.plan.phases = [...state.plan.phases, action.payload];
        }
      })
      .addCase(createPhase.rejected, (state) => { state.saving = false; })

      // Update phase
      .addCase(updatePhase.pending, (state) => { state.saving = true; })
      .addCase(updatePhase.fulfilled, (state, action) => {
        state.saving = false;
        if (state.plan) {
          state.plan.phases = state.plan.phases.map((p) =>
            p.id === action.payload.id ? action.payload : p
          );
        }
      })
      .addCase(updatePhase.rejected, (state) => { state.saving = false; })

      // Delete phase
      .addCase(deletePhase.pending, (state) => { state.saving = true; })
      .addCase(deletePhase.fulfilled, (state, action) => {
        state.saving = false;
        if (state.plan) {
          state.plan.phases = state.plan.phases.filter((p) => p.id !== action.payload);
        }
      })
      .addCase(deletePhase.rejected, (state) => { state.saving = false; })

      // Add story
      .addCase(addStoryToPhase.fulfilled, (state, action) => {
        if (state.plan) {
          state.plan.phases = state.plan.phases.map((p) =>
            p.id === action.payload.id ? action.payload : p
          );
        }
      })

      // Remove story
      .addCase(removeStoryFromPhase.fulfilled, (state, action) => {
        if (state.plan) {
          state.plan.phases = state.plan.phases.map((p) =>
            p.id === action.payload.id ? action.payload : p
          );
        }
      })

      // Search stories
      .addCase(searchStories.pending, (state) => { state.storySearchLoading = true; })
      .addCase(searchStories.fulfilled, (state, action) => {
        state.storySearchLoading = false;
        state.storySearchResults = action.payload;
      })
      .addCase(searchStories.rejected, (state) => {
        state.storySearchLoading = false;
        state.storySearchResults = [];
      });
  },
});

export const { clearPlan, clearStorySearch } = executivePlanSlice.actions;
export default executivePlanSlice.reducer;
